import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { type FileError, useDropzone } from "react-dropzone";
import { BaseButton } from "../BaseButton";
import { BaseIcon } from "../BaseIcons";

const localizeFileError = (fileError: FileError) => {
  switch (fileError.code) {
    case "file-too-large":
      return "ファイルサイズが大きすぎます(上限：10MB)";
    case "file-invalid-type":
      return "対応していないファイル形式です (jpeg, png, pdfのみ)";
    case "too-many-files":
      return "複数のファイルをアップロードすることはできません。";
    default:
      return `エラーが発生しました : ${fileError.message}`;
  }
};

export type BaseFileUploaderUIProps = {
  value?: File[];
  onChange: (files: File[]) => void;
  options?: {
    multiple?: boolean;
  };
};

export const BaseFileUploader = (props: BaseFileUploaderUIProps) => {
  const [allAcceptedFiles, setAllAcceptedFiles] = useState<File[]>(
    props.value ?? [],
  );
  useEffect(() => {
    props.onChange(allAcceptedFiles);
  }, [allAcceptedFiles, props.onChange]);
  const { multiple = true } = props.options ?? {};
  const disabled = !multiple && allAcceptedFiles.length > 0;
  const { getRootProps, getInputProps, fileRejections, isDragActive, open } =
    useDropzone({
      noClick: true,
      multiple,
      disabled,
      accept: {
        "image/*": [],
        "application/pdf": [],
      },
      maxSize: 10 * 1024 * 1024,
      onDrop: (acceptedFiles) => {
        setAllAcceptedFiles((prev) => [...prev, ...acceptedFiles]);
      },
    });
  const rejectedFileErrors = useMemo(() => {
    if (fileRejections.length === 0) {
      return null;
    }
    return fileRejections.map((file) => {
      return {
        name: file.file.name,
        errors: file.errors.map((error) => {
          switch (error?.code) {
            case "file-too-large":
              return "ファイルサイズが大きすぎます(上限：1MB)";
            case "file-invalid-type":
              return "対応していないファイル形式です";
            default:
              return "エラーが発生しました";
          }
        }),
      };
    });
  }, [fileRejections]);
  const hasError = !!rejectedFileErrors;
  const borderColor = hasError ? "#EA583B" : "grey.300";
  const bgColor = hasError
    ? "#FEF4F2"
    : disabled
      ? "grey.50"
      : isDragActive
        ? "grey.100"
        : "white";

  return (
    <Stack spacing={0}>
      <Stack>
        <Stack
          {...getRootProps()}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "160px",
            border: "1px dotted",
            borderColor,
            borderRadius: "12px",
            bgcolor: bgColor,
          }}
        >
          <input type="file" {...getInputProps()} />
          <Stack spacing={2} justifyContent="center" alignItems="center">
            <BaseButton
              color="primary"
              variant="outlined"
              disabled={disabled}
              sx={{ maxWidth: "136px", fontSize: "14px", bgcolor: "white" }}
              onClick={open}
            >
              ファイルを選択
            </BaseButton>
            <Typography variant="body2" fontSize="12px" color="grey.500">
              またはアップロードするファイルをドロップしてください
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={0.5} p={1}>
          {fileRejections?.map((file, i) => (
            <Typography
              key={`${file.file.name}-${i}`}
              variant="caption"
              color="error"
              sx={{ mt: 0.5 }}
            >
              {file.file.name}：
              {file.errors.map((error) => localizeFileError(error)).join(", ")}
            </Typography>
          ))}
        </Stack>
      </Stack>
      <Box>
        {allAcceptedFiles.map((file) => {
          return (
            <Stack
              key={file.name}
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" sx={{ color: "#1364C0" }}>
                {file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setAllAcceptedFiles((prev) => prev.filter((f) => f !== file));
                }}
                sx={{ color: "#1364C0" }}
              >
                <BaseIcon name="xClose" sx={{ fontSize: 12 }} />
              </IconButton>
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};
